import type { StudyTaskReadinessSelectDto, StudyTaskSelectDto } from '@@types/api.types';
import { ClassificationBadge } from '@components/shared/classification-badge.component';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { cn } from '@lib/utils';
import {
  useGetMeiaStudyTargetEngagements,
  useGetMeiaTaskMoes,
  useGetMeiaTaskMops,
  useGetMeiaTaskTechnologies,
} from '@queries/meia.query';
import { GripVerticalIcon, TimerIcon, UserIcon } from 'lucide-react';
import { type CSSProperties, type FC, useMemo } from 'react';
import { getMeiaTaskReadinessStatus } from './meia-task-column.util';
import { MeiaTaskReadinessStatusIcon } from './meia-task-readiness-status-icon.component';
import { MeiaTaskEngagementPills } from './meia-task-technology-engagements.component';

type MeiaTaskCardProps = {
  canDrag: boolean;
  onClick: () => void;
  readiness?: StudyTaskReadinessSelectDto;
  studyId: string;
  task: StudyTaskSelectDto;
};

export const MeiaTaskCard: FC<MeiaTaskCardProps> = (props) => {
  const { canDrag, onClick, readiness, studyId, task } = props;
  const { attributes, isDragging, listeners, setNodeRef, transform, transition } = useSortable({
    data: { swimlaneId: task.swimlaneId, taskId: task.id, type: 'task' },
    disabled: !canDrag,
    id: task.id,
  });
  const skipPerCardQueries = Boolean(readiness);
  const { data: taskMoes = [] } = useGetMeiaTaskMoes({ enabled: !skipPerCardQueries, studyId, taskId: task.id });
  const { data: taskMops = [] } = useGetMeiaTaskMops({ enabled: !skipPerCardQueries, studyId, taskId: task.id });
  const { data: taskTechnologies = [] } = useGetMeiaTaskTechnologies({
    studyId,
    taskId: task.id,
  });
  const { data: studyEngagements = [] } = useGetMeiaStudyTargetEngagements({ studyId });
  const engagements = useMemo(
    () => studyEngagements.filter((engagement) => engagement.taskId === task.id),
    [studyEngagements, task.id],
  );
  const moeCount = readiness ? readiness.moeCount : taskMoes.length;
  const mopCount = readiness ? readiness.mopCount : taskMops.length;
  const technologyCount = readiness ? readiness.technologyCount : taskTechnologies.length;
  const status = readiness
    ? getMeiaTaskReadinessStatus(readiness)
    : getMeiaTaskReadinessStatus({
        moeCount,
        mopAggregation: task.mopAggregation,
        mopCount,
        mosCount: 0,
        scoringRole: task.scoringRole,
      });
  const statusTooltip =
    status === 'complete'
      ? 'This Task has MoS, MoE, MoP, and scoring setup.'
      : 'This Task is missing MoS, MoE, MoP, or scoring setup.';

  const style: CSSProperties = {
    opacity: isDragging ? 0.4 : 1,
    transform: CSS.Translate.toString(transform),
    transition,
  };

  const handleCardClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('[data-drag-handle="true"]')) return;
    onClick();
  };

  return (
    <div
      className={cn(
        'group relative flex cursor-pointer flex-col gap-2 rounded-md border border-gray-200 bg-white p-2.5 text-xs transition-colors hover:border-gray-300',
        isDragging && 'cursor-grabbing',
      )}
      onClick={handleCardClick}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          if (!(event.target instanceof HTMLElement)) return;
          if (event.target.closest('[data-drag-handle="true"]')) return;
          event.preventDefault();
          onClick();
        }
      }}
      ref={setNodeRef}
      role="button"
      style={style}
      tabIndex={0}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-1 items-start gap-2">
          <MeiaTaskReadinessStatusIcon
            className="mt-px"
            status={status}
            tooltip={statusTooltip}
          />
          <p className="line-clamp-2 flex-1 text-xs font-medium leading-snug text-gray-900">{task.taskName}</p>
        </div>
        {canDrag && (
          <button
            aria-label="Drag task"
            className="cursor-grab text-gray-400 hover:text-gray-600 active:cursor-grabbing"
            data-drag-handle="true"
            type="button"
            {...listeners}
            {...attributes}
          >
            <GripVerticalIcon className="size-4" />
          </button>
        )}
      </div>
      {(task.classification || task.owner || task.effort) && (
        <div className="flex flex-wrap items-center gap-1.5 text-[10px] text-gray-500">
          {task.classification && <ClassificationBadge classification={task.classification} />}
          {task.owner && (
            <span className="inline-flex min-w-0 items-center gap-1">
              <UserIcon className="size-3 shrink-0" />
              <span className="truncate">{task.owner}</span>
            </span>
          )}
          {task.effort && (
            <span className="inline-flex items-center gap-1">
              <TimerIcon className="size-3" />
              {task.effort}
            </span>
          )}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-1.5 text-[10px] text-gray-500">
        {moeCount > 0 && (
          <span className="rounded-sm bg-pink-50 px-1.5 py-0.5 font-medium text-pink-700">{moeCount} MoE</span>
        )}
        {mopCount > 0 && (
          <span className="rounded-sm bg-blue-50 px-1.5 py-0.5 font-medium text-blue-700">{mopCount} MoP</span>
        )}
        {technologyCount > 0 && (
          <span className="rounded-sm bg-gray-100 px-1.5 py-0.5 font-medium text-gray-700">
            {technologyCount} {technologyCount === 1 ? 'Technology' : 'Technologies'}
          </span>
        )}
      </div>
      <MeiaTaskEngagementPills engagements={engagements} />
    </div>
  );
};
